import React from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  CreditCard,
  Banknote,
  Wallet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PaymentStatus, DashboardPeriod } from '@/types/salesDashboard';

// Format currency in Indian format
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

interface PaymentCardProps {
  title: string;
  count: number;
  amount: number;
  icon: React.ElementType;
  colorScheme: {
    bg: string;
    border: string;
    text: string;
    iconBg: string;
  };
  delay?: number;
  onClick?: () => void;
  tooltip?: string;
}

const PaymentCard: React.FC<PaymentCardProps> = ({
  title,
  count,
  amount,
  icon: Icon,
  colorScheme,
  delay = 0,
  onClick,
  tooltip,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick?.() }}
      className={`${colorScheme.bg} ${colorScheme.border} border rounded-lg p-4 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:outline-none`}
      title={tooltip}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-sm font-medium ${colorScheme.text}`}>{title}</p>
          <p className={`text-xl font-bold ${colorScheme.text} mt-1`}>
            {formatCurrency(amount)}
            <span className="text-sm font-normal ml-1">({count})</span>
          </p>
        </div>
        <div className={`${colorScheme.iconBg} p-2 rounded-lg`}>
          <Icon className={`w-5 h-5 ${colorScheme.text}`} />
        </div>
      </div>
    </motion.div>
  );
};

interface PaymentStatusSectionProps {
  payments: PaymentStatus;
  period: DashboardPeriod;
  isLoading?: boolean;
  onCardClick?: (type: 'received' | 'pending' | 'overdue' | 'online' | 'cash') => void;
}

export const PaymentStatusSection: React.FC<PaymentStatusSectionProps> = ({
  payments,
  period,
  isLoading,
  onCardClick,
}) => {
  const periodLabel = {
    today: 'Today',
    week: 'This Week',
    month: 'This Month',
  }[period];

  const paymentCards: Array<{
    key: 'received' | 'pending' | 'overdue';
    title: string;
    count: number;
    amount: number;
    icon: React.ElementType;
    colorScheme: { bg: string; border: string; text: string; iconBg: string };
    tooltip?: string;
  }> = [
    {
      key: 'received' as const,
      title: 'Cash In (MTD)',
      count: payments.received.count,
      amount: payments.received.amount,
      icon: CheckCircle2,
      colorScheme: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-700',
        iconBg: 'bg-green-100',
      },
      tooltip: 'Payments received this month.',
    },
    {
      key: 'pending' as const,
      title: 'Payments Pending',
      count: payments.pending.count,
      amount: payments.pending.amount,
      icon: Clock,
      colorScheme: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-700',
        iconBg: 'bg-yellow-100',
      },
    },
    {
      key: 'overdue' as const,
      title: 'Payments Overdue',
      count: payments.overdue.count,
      amount: payments.overdue.amount,
      icon: AlertCircle,
      colorScheme: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        iconBg: 'bg-red-100',
      },
    },
  ];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Payment Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Wallet className="w-5 h-5 text-indigo-600" />
            Payment Status
          </CardTitle>
          <div
            className="flex items-center gap-2 text-sm px-3 py-1 rounded-full bg-gray-100 border border-gray-200"
            title="Cumulative cash collected across ALL TIME (not period-scoped). Distinct from 'Cash In (MTD)' below, which is scoped to the current month only."
          >
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Lifetime Cash In
            </span>
            <span className="font-semibold text-gray-900">
              {formatCurrency(payments.totalReceived)}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Payment Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {paymentCards.map((card, index) => (
            <PaymentCard
              key={card.key}
              title={card.title}
              count={card.count}
              amount={card.amount}
              icon={card.icon}
              colorScheme={card.colorScheme}
              delay={index * 0.1}
              onClick={() => onCardClick?.(card.key)}
              tooltip={card.tooltip}
            />
          ))}
        </div>

        {/* Payment Method Breakdown */}
        <div className="grid grid-cols-2 gap-4 pt-2">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            onClick={() => onCardClick?.('online')}
            className="bg-blue-50 border border-blue-100 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Online Payments</span>
            </div>
            <div className="flex justify-between items-end">
              <span className="text-lg font-bold text-blue-800">
                {formatCurrency(payments.byMethod.online.amount)}
              </span>
              <span className="text-sm text-blue-600">
                {payments.byMethod.online.count} transactions
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            onClick={() => onCardClick?.('cash')}
            className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-2 mb-2">
              <Banknote className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Cash/Cheque</span>
            </div>
            <div className="flex justify-between items-end">
              <span className="text-lg font-bold text-emerald-800">
                {formatCurrency(payments.byMethod.cash.amount)}
              </span>
              <span className="text-sm text-emerald-600">
                {payments.byMethod.cash.count} transactions
              </span>
            </div>
          </motion.div>
        </div>
      </CardContent>
    </Card>
  );
};
